<template>
    <component-loader class="over-component" v-if="isLoading" />
    <span v-else-if="!props.conversationHistory || Object.keys(props.conversationHistory).length === 0">No chat history recorded</span>
    <div class="content" v-else>
        <span v-if="hasTodayConversations" class="header">Recent</span>
        <div v-for="(conversation, id) in past24HoursConversations" :key="id" class="conversation-history-holder">
            <prime-button class="conversation-history-item" @click="saveConversationId(id, conversation.documentId)">
                <div class="item-text">
                    <span class="conversation-item-title">{{ conversation.question }}</span>
                    <span class="conversation-item-sub-title" v-if="conversation.documentId">{{ getDocumentTitleByShortCode(conversation.documentId) }}</span>
                </div>
                <div class="item-icon">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </div>
            </prime-button>
        </div>
        <span v-if="hasThisWeekConversations" class="header">This Week</span>
        <div v-for="(conversation, id) in thisWeekConversations" :key="id" class="conversation-history-holder">
            <prime-button class="conversation-history-item" @click="saveConversationId(id, conversation.documentId)">
                <div class="item-text">
                    <span class="conversation-item-title">{{ conversation.question }}</span>
                    <span class="conversation-item-sub-title" v-if="conversation.documentId">{{ getDocumentTitleByShortCode(conversation.documentId) }}</span>
                </div>
                <div class="item-icon">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </div>
            </prime-button>
        </div>
        <span v-if="hasBeforeLastWeekConversations" class="header">Older</span>
        <div v-for="(conversation, id) in beforeLastWeekConversations" :key="id" class="conversation-history-holder">
            <prime-button class="conversation-history-item" @click="saveConversationId(id, conversation.documentId)">
                <div class="item-text">
                    <span class="conversation-item-title">{{ conversation.question }}</span>
                    <span class="conversation-item-sub-title" v-if="conversation.documentId">{{ getDocumentTitleByShortCode(conversation.documentId) }}</span>
                </div>
                <div class="item-icon">
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                </div>
            </prime-button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { IConversationSummary } from '@/interfaces/deltaGpt/IConversationSummary.ts';
import { useDeltaGptHistoryStore } from '@/stores/deltaGpt/deltaGptHistoryStore.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useRouter } from 'vue-router';
import PrimeButton from 'primevue/button';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import ComponentLoader from '../layout/ComponentLoader.vue';

dayjs.extend(isBetween);

const props = defineProps<{
    conversationHistory?: Record<string, IConversationSummary>;
}>();

const emit = defineEmits<{
    'close': [];
}>();

const deltaGptHistoryStore = useDeltaGptHistoryStore();
const deltaGptStore = useDeltaGptStore();
const router = useRouter();

const isLoading = ref<boolean>(false);

const hasTodayConversations = computed<boolean>(() => Object.values(past24HoursConversations.value).length > 0);
const hasThisWeekConversations = computed<boolean>(() => Object.values(thisWeekConversations.value).length > 0);
const hasBeforeLastWeekConversations = computed<boolean>(() => Object.values(beforeLastWeekConversations.value).length > 0);

const past24HoursConversations = computed<Record<string, IConversationSummary>>(() => {
    const now = dayjs();
    const past24Hours = now.subtract(24, 'hour');

    const filteredConversations = Object.entries(props.conversationHistory || {}).filter(([, conversation]) => {
        const conversationTime = dayjs(conversation.time);
        return conversationTime.isAfter(past24Hours);
    });

    return Object.fromEntries(filteredConversations);
});

const thisWeekConversations = computed<Record<string, IConversationSummary>>(() => {
    const now = dayjs();
    const startOfLastWeek = now.subtract(1, 'week').startOf('day');
    const startOfToday = now.startOf('day');

    const filteredConversations = Object.entries(props.conversationHistory || {}).filter(([, conversation]) => {
        const conversationTime = dayjs(conversation.time);
        return conversationTime.isBetween(startOfLastWeek, startOfToday, null, '[]');
    });

    return Object.fromEntries(filteredConversations);
});

const beforeLastWeekConversations = computed<Record<string, IConversationSummary>>(() => {
    const now = dayjs();
    const startOfLastWeek = now.subtract(1, 'week').startOf('day');

    const filteredConversations = Object.entries(props.conversationHistory || {}).filter(([, conversation]) => {
        const conversationTime = dayjs(conversation.time);
        return conversationTime.isBefore(startOfLastWeek, 'day');
    });

    return Object.fromEntries(filteredConversations);
});

const documentTitleMap = computed<Record<string, string>>(() => deltaGptStore.allDocumentsSimple.reduce((map, doc) => {
    map[doc.documentShortCode] = doc.documentTitle;
    return map;
}, {} as Record<string, string>));

async function saveConversationId (id: string, documentId: string | null): Promise<void> {
    deltaGptHistoryStore.currentChatHistoryId = id;
    const history = await deltaGptHistoryStore.getChatMessageHistoryById(id);
    const routeName = documentId ? deltaGptRoutes.documentView.name : deltaGptRoutes.documentList.name;
    await router.push({ name: routeName, params: { documentShortCode: documentId } });
    deltaGptHistoryStore.currentChatMessageHistory = history;
    deltaGptHistoryStore.redirectFromHistory = true;
    emit('close');
}

function getDocumentTitleByShortCode (shortCode: string): string {
    return documentTitleMap.value[shortCode] || 'Unknown Document';
}

async function initialise (): Promise<void> {
    isLoading.value = true;
    await deltaGptHistoryStore.GetUsersConversationSummary();
    isLoading.value = false;
}

initialise();

</script>

<style lang="scss" scoped>
.content {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow-y: auto;

    .header {
        font-weight: 600;
        padding: var(--defaultSpacing);
    }

    .conversation-history-holder {
        padding: 0 var(--smallSpacing);

        .conversation-history-item {
            display: grid;
            grid-template-columns: 10fr 1fr;
            background-color: var(--neutral300);
            border: 2px solid var(--neutral200);
            padding: var(--largeSpacing);
            border-radius: var(--borderRadiusOuter);
            transition: background-color 0.3s ease, box-shadow 0.3s ease;
            cursor: pointer;
            position: relative;
            text-align: left;
            width: 100%;

            .item-icon {
                padding: var(--defaultSpacing);
                color: var(--actionColour);
            }

            .item-text {
                display: flex;
                flex-direction: column;
            }

            .conversation-item-title {
                color: var(--primaryFont);
                margin-bottom: var(--smallSpacing);
            }

            .conversation-item-sub-title {
                font-size: var(--extraSmallFontSize);
                color: var(--accentColour);
            }
        }

    }

    .conversation-history-item:hover {
        background-color: var(--neutral400);
    }

}

</style>
